import { useState } from 'react';
import { errorMessage, useLocale, type MessageKey } from '../../i18n';
import { StatusMessage } from '../ui';
import {
  fetchPaymentExceptions as defaultFetch,
  triageLevel,
  type ExceptionWorklist,
  type WorklistResult,
} from '../../lib/api/platformPaymentExceptions';

// ─────────────────────────────────────────────────────────────────────
// «BETALINGSAVVIK» (#1131, AUD-014) — the payment-exception worklist in /sa.
//
// The daily procedure is `docs/operations/PAYMENT_EXCEPTIONS.md`; until this
// its two lists needed `psql`. This shows them: the payments carrying an
// exception code, most urgent first, and the provider facts nothing matched.
//
// READ-ONLY, ON PURPOSE. What to do with a row is the runbook's — done in
// Stripe and with the organization — and there is no button here that moves
// money or clears a code. The server decides who may read and records every
// read, so the list is fetched only when asked, never on opening the section.
//
// The triage LEVEL is shown through the catalog; the runbook's sentence and
// the codes are shown as the server sends them — they are identifiers a
// person looks up in the runbook, not product copy.
// ─────────────────────────────────────────────────────────────────────

export interface PaymentExceptionsSectionProps {
  /** Injectable for tests; defaults to the real client. */
  load?: () => Promise<WorklistResult>;
}

type Outcome =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; worklist: ExceptionWorklist }
  | { kind: 'failed'; text: string };

const LEVEL_KEY: Record<0 | 1 | 2 | 3, MessageKey> = {
  0: 'saPayEx.level.0',
  1: 'saPayEx.level.1',
  2: 'saPayEx.level.2',
  3: 'saPayEx.level.3',
};

/** Level 3 asks a person to act: it is the only one drawn as a warning. */
const LEVEL_CLASS: Record<0 | 1 | 2 | 3, string> = {
  0: 'bg-white/5 text-ink-3',
  1: 'bg-white/5 text-ink-2',
  2: 'bg-amber-500/15 text-amber-200',
  3: 'bg-red-500/20 text-red-200',
};

export function PaymentExceptionsSection({ load = defaultFetch }: PaymentExceptionsSectionProps) {
  const { t, formatDate, formatNumber } = useLocale();
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' });

  const refusal = (result: Exclude<WorklistResult, { state: 'ok' }>): string => {
    if (result.state === 'no-api') return t('saPayEx.error.noApi');
    if (result.state === 'error') return t('saPayEx.error.failed');
    if (result.code === 'forbidden') return t('saPayEx.error.forbidden');
    if (result.code === 'entra_required') return t('saPayEx.error.entraRequired');
    return errorMessage(t, result.code);
  };

  const read = async () => {
    setOutcome({ kind: 'loading' });
    const result = await load();
    setOutcome(
      result.state === 'ok'
        ? { kind: 'ok', worklist: result.worklist }
        : { kind: 'failed', text: refusal(result) },
    );
  };

  const when = (iso: string) =>
    formatDate(new Date(iso), { dateStyle: 'medium', timeStyle: 'short' });
  const money = (minor: number, currency: string) =>
    formatNumber(minor / 100, { style: 'currency', currency });
  const th = 'py-1 pr-3 font-medium align-bottom';
  const td = 'py-1.5 pr-3 align-top';

  const worklist = outcome.kind === 'ok' ? outcome.worklist : null;

  return (
    <section className="card p-6" data-testid="sa-payment-exceptions">
      <h1 className="font-display text-2xl font-semibold text-ink">{t('saPayEx.title')}</h1>
      <p className="text-sm text-ink-3 mt-1">{t('saPayEx.lead')}</p>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          className="btn-lime text-sm"
          onClick={() => void read()}
          disabled={outcome.kind === 'loading'}
          data-testid="sa-payment-exceptions-read"
        >
          {worklist ? t('saPayEx.refresh') : t('saPayEx.read')}
        </button>
        {outcome.kind === 'loading' && (
          <span className="text-xs text-ink-3" role="status">
            {t('saPayEx.loading')}
          </span>
        )}
      </div>

      {outcome.kind === 'failed' && (
        <div className="mt-3" data-testid="sa-payment-exceptions-error">
          <StatusMessage>{outcome.text}</StatusMessage>
        </div>
      )}

      {worklist && (
        <>
          <h2 className="font-display text-lg font-semibold text-ink mt-6">
            {t('saPayEx.payments.heading', { count: worklist.payments.length })}
          </h2>
          {worklist.payments.length === 0 ? (
            <p className="text-sm text-ink-3 mt-1" data-testid="sa-payment-exceptions-none">
              {t('saPayEx.payments.empty')}
            </p>
          ) : (
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-xs" data-testid="sa-payment-exceptions-payments">
                <caption className="sr-only">{t('saPayEx.payments.caption')}</caption>
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-ink-3">
                    <th scope="col" className={th}>
                      {t('saPayEx.col.level')}
                    </th>
                    <th scope="col" className={th}>
                      {t('saPayEx.col.organization')}
                    </th>
                    <th scope="col" className={th}>
                      {t('saPayEx.col.reason')}
                    </th>
                    <th scope="col" className={th}>
                      {t('saPayEx.col.state')}
                    </th>
                    <th scope="col" className={`${th} text-right`}>
                      {t('saPayEx.col.amount')}
                    </th>
                    <th scope="col" className={th}>
                      {t('saPayEx.col.booking')}
                    </th>
                    <th scope="col" className={th}>
                      {t('saPayEx.col.updated')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {worklist.payments.map((p) => {
                    const level = triageLevel(p.triage);
                    return (
                      <tr
                        key={p.paymentId}
                        className="border-t border-white/10"
                        data-testid={`sa-payment-exceptions-payment-${p.paymentId}`}
                      >
                        <td className={td}>
                          <span
                            className={`inline-block rounded px-1.5 py-0.5 font-medium ${level === null ? LEVEL_CLASS[3] : LEVEL_CLASS[level]}`}
                          >
                            {level === null ? t('saPayEx.level.unknown') : t(LEVEL_KEY[level])}
                          </span>
                          <div className="text-ink-3 mt-1 max-w-[18rem]">{p.triage}</div>
                        </td>
                        <td className={td}>{p.organization}</td>
                        <td className={`${td} font-mono`}>{p.exceptionReason}</td>
                        <td className={`${td} font-mono`}>{p.state}</td>
                        <td className={`${td} text-right tabular-nums`}>
                          {money(p.amountMinor, p.currency)}
                        </td>
                        <td className={td}>
                          {p.bookingExists
                            ? (p.bookingPaymentStatus ?? '–')
                            : t('saPayEx.booking.gone')}
                          <div className="font-mono text-ink-3 mt-1">{p.paymentId}</div>
                        </td>
                        <td className={`${td} whitespace-nowrap`}>{when(p.updatedAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {worklist.paymentsTruncated && (
            <p className="text-xs text-ink-3 mt-2" data-testid="sa-payment-exceptions-truncated">
              {t('saPayEx.truncated', { count: worklist.payments.length })}
            </p>
          )}

          <h2 className="font-display text-lg font-semibold text-ink mt-6">
            {t('saPayEx.unmatched.heading', { count: worklist.unmatched.length })}
          </h2>
          {worklist.unmatched.length === 0 ? (
            <p className="text-sm text-ink-3 mt-1">{t('saPayEx.unmatched.empty')}</p>
          ) : (
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-xs" data-testid="sa-payment-exceptions-unmatched">
                <caption className="sr-only">{t('saPayEx.unmatched.caption')}</caption>
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-ink-3">
                    <th scope="col" className={th}>
                      {t('saPayEx.col.event')}
                    </th>
                    <th scope="col" className={th}>
                      {t('saPayEx.col.provider')}
                    </th>
                    <th scope="col" className={th}>
                      {t('saPayEx.col.process')}
                    </th>
                    <th scope="col" className={`${th} text-right`}>
                      {t('saPayEx.col.deliveries')}
                    </th>
                    <th scope="col" className={th}>
                      {t('saPayEx.col.firstSeen')}
                    </th>
                    <th scope="col" className={th}>
                      {t('saPayEx.col.lastSeen')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {worklist.unmatched.map((u) => (
                    <tr key={u.providerEventId} className="border-t border-white/10">
                      <td className={`${td} font-mono`}>{u.providerEventId}</td>
                      <td className={td}>{u.provider ?? '–'}</td>
                      <td className={td}>{u.process ?? '–'}</td>
                      <td className={`${td} text-right tabular-nums`}>
                        {formatNumber(u.deliveries)}
                      </td>
                      <td className={`${td} whitespace-nowrap`}>{when(u.firstSeen)}</td>
                      <td className={`${td} whitespace-nowrap`}>{when(u.lastSeen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {worklist.unmatchedTruncated && (
            <p className="text-xs text-ink-3 mt-2">
              {t('saPayEx.truncated', { count: worklist.unmatched.length })}
            </p>
          )}
        </>
      )}
    </section>
  );
}
